import { type Routine, useStores } from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { MeetingFeedbackModal } from '@/components/modals/MeetingFeedbackModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { IconButton } from '@/components/ui';
import {
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { useResumeRoutine } from '@/features/routines/hooks/use-resume-routine';
import { getRoutineActionsWithLatestRun } from '@/features/routines/routine-action-state';
import { VoteDataDirection, type VoteDataDirectionType } from '@/query/vote';
import { trackEvent } from '@/utils/analytics';

import { useDeleteRoutine } from '../hooks/use-routine-actions';
import { getSavedRoutineView } from '../routine-chat-view';
import { markRoutineConfigurationEntry } from '../routine-configuration-history';
import { routinesStore } from '../routines.state';

type RoutineView = 'canvas' | 'chat' | 'configure';
type MenuVariant = 'context' | 'dropdown';

type MenuItemProps = {
  variant: MenuVariant;
  Icon: LBIcon;
  children: string;
  disabled?: boolean;
  danger?: boolean;
  onClick?: () => void;
};

const MenuItem = ({
  variant,
  Icon,
  children,
  disabled,
  danger,
  onClick,
}: MenuItemProps) =>
  variant === 'context' ? (
    <ContextMenuItem
      disabled={disabled}
      className={danger ? 'text-danger-9' : undefined}
      icon={Icon}
      onClick={onClick}
    >
      {children}
    </ContextMenuItem>
  ) : (
    <DropdownMenu.Item
      disabled={disabled}
      className={danger ? 'text-danger-9' : undefined}
      onClick={onClick}
    >
      <Icon />
      {children}
    </DropdownMenu.Item>
  );

const MenuLabel = ({
  variant,
  children,
}: {
  variant: MenuVariant;
  children: ReactNode;
}) =>
  variant === 'context' ? (
    <ContextMenuLabel>
      <span className="text-xs.5">{children}</span>
    </ContextMenuLabel>
  ) : (
    <DropdownMenu.Label>
      <span className="text-xs.5">{children}</span>
    </DropdownMenu.Label>
  );

const MenuSeparator = ({ variant }: { variant: MenuVariant }) =>
  variant === 'context' ? <ContextMenuSeparator /> : <DropdownMenu.Separator />;

type RoutineActionsMenuItemsProps = {
  variant: MenuVariant;
  routine: Routine;
  statusLabel: string;
  hasCanvas: boolean;
  view?: RoutineView;
  reportId?: number | null;
  source?: 'header' | 'sidebar';
  onNavigate?: () => void;
};

export const RoutineActionsMenuItems = observer(
  ({
    variant,
    routine,
    statusLabel,
    hasCanvas,
    view,
    reportId,
    source = 'header',
    onNavigate,
  }: RoutineActionsMenuItemsProps) => {
    const navigate = useNavigate();
    const { reports, routines } = useStores();
    const resumeRoutine = useResumeRoutine();
    const { showModal: showDeleteRoutineModal } = useDeleteRoutine();
    const isGenerating = routines.isOperationPending(
      'generate',
      routine.referenceId
    );
    const isPausing = routines.isOperationPending('pause', routine.referenceId);
    const isResuming = routines.isOperationPending(
      'resume',
      routine.referenceId
    );
    const storedReportId = routinesStore.useSelectedReportId(
      routine.referenceId
    );
    const selectedReportId = reportId === undefined ? storedReportId : reportId;

    const actions = getRoutineActionsWithLatestRun(
      routine,
      reports,
      isGenerating
    );
    const prepareNavigation = () => {
      onNavigate?.();
      if (source === 'sidebar') routinesStore.unpinReport(routine.referenceId);
    };
    const showRuns = () => {
      prepareNavigation();
      routinesStore.setShowHistory(true);
      void navigate({
        to: '/routines/$routineId',
        params: { routineId: String(routine.referenceId) },
        search: (previous) =>
          source === 'sidebar'
            ? { view: getSavedRoutineView(routine.referenceId) }
            : { ...previous, view: getSavedRoutineView(routine.referenceId) },
        replace: source !== 'sidebar',
      });
    };
    const toggleCanvasView = () => {
      prepareNavigation();
      void navigate({
        to: '/routines/$routineId',
        params: { routineId: String(routine.referenceId) },
        search: (previous) => {
          const nextView = view === 'canvas' ? 'chat' : 'canvas';
          return source === 'sidebar'
            ? { view: nextView }
            : { ...previous, view: nextView };
        },
      });
    };
    const showConfiguration = () => {
      prepareNavigation();
      routinesStore.setShowHistory(true);
      const returnsToCurrentView = source === 'header' || view !== undefined;
      void navigate({
        to: '/routines/$routineId',
        params: { routineId: String(routine.referenceId) },
        search: (previous) =>
          source === 'sidebar'
            ? { view: 'configure' }
            : { ...previous, view: 'configure' },
        ...(returnsToCurrentView && {
          state: markRoutineConfigurationEntry,
        }),
      });
    };

    const openFeedback = useCallback(() => {
      if (!selectedReportId) return;
      const activeReportId = selectedReportId;

      ModalsManager.show(MeetingFeedbackModal, {
        description:
          'We review all feedback to improve your routine reports experience',
        onSubmit: async (
          direction: VoteDataDirectionType,
          reasoning: string
        ) => {
          const isUp = direction === VoteDataDirection.UP;

          try {
            await reports.submitFeedback(
              activeReportId,
              direction,
              reasoning.trim() || null
            );

            trackEvent('ROUTINE_REPORT_RATED', {
              report_id: activeReportId,
              direction: isUp ? 'up' : 'down',
              has_reasoning: !!reasoning.trim(),
            });

            toast.info('Thank you for your feedback!');
          } catch (error) {
            toast.error('Failed to submit feedback. Please try again.');
            throw error;
          }
        },
      });
    }, [reports, selectedReportId]);

    return (
      <>
        <MenuLabel variant={variant}>{statusLabel}</MenuLabel>
        <MenuSeparator variant={variant} />
        {!routine.isPaused && actions.permissions.canRun && (
          <MenuItem
            variant={variant}
            Icon={Icons.playAlt}
            disabled={!actions.canRun}
            onClick={() =>
              void routines
                .generateReport(routine.referenceId)
                .catch(() => undefined)
            }
          >
            Run
          </MenuItem>
        )}
        <MenuItem variant={variant} Icon={Icons.history} onClick={showRuns}>
          History
        </MenuItem>
        {hasCanvas && (
          <MenuItem
            variant={variant}
            Icon={view === 'canvas' ? Icons.chat : Icons.layoutFreeform}
            onClick={toggleCanvasView}
          >
            {view === 'canvas' ? 'View chat' : 'View Canvas'}
          </MenuItem>
        )}
        <MenuItem
          variant={variant}
          Icon={routine.starred ? Icons.starFilled : Icons.star}
          onClick={() => {
            const starred = !routine.starred;
            void routines
              .setRoutineStarred(routine.referenceId, starred)
              .catch(() => undefined);
            trackEvent('ROUTINE_STARRED', {
              favorite: starred,
              origin: source,
            });
          }}
        >
          {routine.starred ? 'Unfavorite' : 'Favorite'}
        </MenuItem>
        <MenuSeparator variant={variant} />
        {actions.canConfigure && (
          <MenuItem
            variant={variant}
            Icon={Icons.slidersHorizontal}
            onClick={showConfiguration}
          >
            Configure
          </MenuItem>
        )}
        {routine.isAdminPaused ? (
          <MenuItem
            variant={variant}
            Icon={Icons.pause}
            disabled
            onClick={() => undefined}
          >
            Paused by admin
          </MenuItem>
        ) : actions.canResume ? (
          <MenuItem
            variant={variant}
            Icon={Icons.playAlt}
            disabled={isResuming}
            onClick={() => void resumeRoutine(routine.referenceId)}
          >
            Resume
          </MenuItem>
        ) : actions.canPause ? (
          <MenuItem
            variant={variant}
            Icon={Icons.pause}
            disabled={isPausing}
            onClick={() =>
              void routines
                .pauseRoutine(routine.referenceId)
                .catch(() => undefined)
            }
          >
            Pause
          </MenuItem>
        ) : null}
        <MenuItem
          variant={variant}
          Icon={Icons.feedback}
          disabled={!selectedReportId}
          onClick={openFeedback}
        >
          Feedback
        </MenuItem>
        {actions.canDelete && (
          <>
            <MenuSeparator variant={variant} />
            <MenuItem
              variant={variant}
              Icon={Icons.trash}
              danger
              onClick={() => void showDeleteRoutineModal(routine)}
            >
              Delete
            </MenuItem>
          </>
        )}
      </>
    );
  }
);

type RoutineActionsMenuProps = Omit<
  RoutineActionsMenuItemsProps,
  'variant' | 'reportId' | 'source' | 'onNavigate'
>;

export const RoutineActionsMenu = observer(
  ({ routine, statusLabel, hasCanvas, view }: RoutineActionsMenuProps) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if (!open) return;
      const closeMenu = () => setOpen(false);
      window.addEventListener('blur', closeMenu);
      return () => window.removeEventListener('blur', closeMenu);
    }, [open]);

    return (
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <IconButton
            size="xs"
            icon={Icons.moreHorizontal}
            aria-label={`Actions for ${routine.title}`}
            className="self-start data-[state=open]:bg-secondary-hover"
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start" className="min-w-52" fit>
          <RoutineActionsMenuItems
            variant="dropdown"
            routine={routine}
            statusLabel={statusLabel}
            hasCanvas={hasCanvas}
            view={view}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);
